import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';
import type { EventOutcomePriority } from '../event-outcome/event-outcome.types';
import {
  isAllOutcomePrioritiesSelected,
  isOutcomePrioritySelected,
  toggleOutcomePrioritySelection,
} from '../event-outcome/event-outcome.types';

export interface EventqPriorityFilterOption {
  id: EventOutcomePriority | 'all';
  label: string;
  iconSrc?: string;
}

@Component({
  selector: 'eventq-priority-filter-row',
  imports: [PrefixUrlPipe],
  templateUrl: './priority-filter-row.html',
  styleUrl: './priority-filter-row.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqPriorityFilterRow {
  readonly options = input.required<EventqPriorityFilterOption[]>();
  readonly selectedPriorities = input<EventOutcomePriority[]>([]);
  /**
   * When true (default), empty selection means all priorities (Agent Raise/Close).
   * When false, empty means none selected (Resolve Queue).
   */
  readonly emptyMeansAll = input(true);

  readonly selectedPrioritiesChange = output<EventOutcomePriority[]>();

  private availablePriorities(): EventOutcomePriority[] {
    return this.options()
      .filter(
        (option): option is EventqPriorityFilterOption & { id: EventOutcomePriority } =>
          option.id !== 'all',
      )
      .map((option) => option.id);
  }

  isAllActive(): boolean {
    return isAllOutcomePrioritiesSelected(
      this.selectedPriorities(),
      this.availablePriorities(),
      this.emptyMeansAll(),
    );
  }

  isPrioritySelected(id: EventOutcomePriority): boolean {
    return isOutcomePrioritySelected(
      id,
      this.selectedPriorities(),
      this.availablePriorities(),
      this.emptyMeansAll(),
    );
  }

  isFilterSelected(id: EventOutcomePriority | 'all'): boolean {
    if (id === 'all') {
      return this.isAllActive();
    }

    if (this.isAllActive()) {
      return this.emptyMeansAll();
    }

    return this.isPrioritySelected(id);
  }

  onSelect(id: EventOutcomePriority | 'all'): void {
    this.selectedPrioritiesChange.emit(
      toggleOutcomePrioritySelection(
        id,
        this.selectedPriorities(),
        this.availablePriorities(),
        this.emptyMeansAll(),
      ),
    );
  }
}
